/**
 * Chargement d'une vue du monitoring (F-FE-01, F-FE-02, F-FE-03).
 *
 * - Annulation : chaque nouvelle requête annule la précédente
 *   (`AbortController`), et un numéro de séquence garantit que SEULE la
 *   dernière réponse s'applique — une réponse « 12 mois » lente n'écrase plus
 *   une réponse « 7 jours » plus récente.
 * - États distincts : `loading` (aucune donnée pour ces filtres : squelettes),
 *   `refreshing` (mêmes filtres, on garde l'affichage), `error` (données
 *   effacées), `forbidden_scope` (403 de périmètre : les filtres restent),
 *   `role_required` (403 de rôle).
 * - `refreshToken` : compteur partagé par tous les onglets ; l'incrémenter
 *   recharge chaque vue active, y compris les traces.
 * - `enabled` : un onglet inactif ne charge rien ; redevenu actif, il ne
 *   recharge que si ses filtres ou le `refreshToken` ont changé entre-temps.
 * - Le `fetcher` est lu par une ref : les fonctions d'API changent d'identité
 *   à chaque rendu (mémoire projet), elles ne pilotent jamais le rechargement.
 *   Seule la `key` (sérialisation des paramètres) le fait.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export type QueryStatus = 'idle' | 'loading' | 'refreshing' | 'success' | 'error' | 'forbidden_scope' | 'role_required';

export type QueryError = {
    httpStatus: number | null;
    /** Code `error` du corps de la réponse (contrat §1), s'il existe. */
    code: string | null;
};

export type QueryState<T> = {
    status: QueryStatus;
    data: T | null;
    error: QueryError | null;
};

export type MonitoringQueryOptions = {
    refreshToken?: number;
    enabled?: boolean;
    /**
     * Groupe de la requête (par défaut : la clé). Tant que le groupe ne change
     * pas — par exemple une page ou un tri de tableau pour les mêmes filtres —
     * le rechargement est un `refreshing` : l'affichage courant reste en place.
     * Un nouveau groupe (autres filtres) efface les données : `loading`.
     */
    groupKey?: string;
};

export const isAbortError = (err: unknown): boolean => {
    const e = err as { name?: string; code?: string } | null;
    return !!e && (e.name === 'CanceledError' || e.name === 'AbortError' || e.code === 'ERR_CANCELED');
};

/** Classement d'une erreur HTTP selon le contrat §1. */
export const classifyError = (err: unknown): { status: QueryStatus; error: QueryError } => {
    const response = (err as { response?: { status?: number; data?: unknown } } | null)?.response;
    const httpStatus = typeof response?.status === 'number' ? response.status : null;
    const body = response?.data as { error?: unknown } | undefined;
    const code = body && typeof body.error === 'string' ? body.error : null;
    if (httpStatus === 403 && code === 'scope_forbidden') return { status: 'forbidden_scope', error: { httpStatus, code } };
    if (httpStatus === 403 && code === 'role_required') return { status: 'role_required', error: { httpStatus, code } };
    return { status: 'error', error: { httpStatus, code } };
};

export function useMonitoringQuery<T>(
    key: string,
    fetcher: (signal: AbortSignal) => Promise<T>,
    { refreshToken = 0, enabled = true, groupKey }: MonitoringQueryOptions = {}
): QueryState<T> & { reload: () => void } {
    const fetcherRef = useRef(fetcher);
    fetcherRef.current = fetcher;

    const seqRef = useRef(0);
    /** Clé, groupe et jeton des données actuellement affichées. */
    const shownRef = useRef<{ key: string; group: string; token: string } | null>(null);
    const group = groupKey ?? key;

    const [localToken, setLocalToken] = useState(0);
    const [state, setState] = useState<QueryState<T>>({
        status: enabled ? 'loading' : 'idle',
        data: null,
        error: null,
    });

    useEffect(() => {
        if (!enabled) return undefined;
        const token = `${refreshToken}|${localToken}`;
        const shown = shownRef.current;
        if (shown && shown.key === key && shown.token === token) return undefined; // déjà à jour

        const isRefresh = shown !== null && shown.group === group;
        if (!isRefresh) shownRef.current = null;
        setState((prev) =>
            isRefresh
                ? { status: 'refreshing', data: prev.data, error: null }
                : { status: 'loading', data: null, error: null }
        );

        const seq = ++seqRef.current;
        const controller = new AbortController();

        fetcherRef
            .current(controller.signal)
            .then((data) => {
                if (seq !== seqRef.current) return; // réponse périmée : ignorée
                shownRef.current = { key, group, token };
                setState({ status: 'success', data, error: null });
            })
            .catch((err: unknown) => {
                if (seq !== seqRef.current || isAbortError(err)) return;
                shownRef.current = null;
                const { status, error } = classifyError(err);
                // Erreur : les anciennes données sont effacées, jamais laissées
                // à l'écran comme si elles valaient pour les filtres courants.
                setState({ status, data: null, error });
            });

        return () => {
            controller.abort();
        };
    }, [key, group, refreshToken, localToken, enabled]);

    const reload = useCallback(() => setLocalToken((n) => n + 1), []);

    return { ...state, reload };
}
